import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((item) => item.id === id);

  usePageMeta(
    article ? `${article.title}-奇优影院影视资讯` : "内容不存在-奇优影院",
    article ? article.summary : "未找到对应的资讯内容，返回影视资讯专题查看更多内容。",
    "影视资讯,奇优影院",
  );

  if (!article) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="text-xl md:text-2xl font-extrabold text-foreground-950 font-heading mt-4">
            内容不存在
          </h1>
          <p className="text-sm text-foreground-600 mt-2">
            这篇资讯可能已被移除或链接有误，去资讯专题看看其它内容吧。
          </p>
          <Link
            to="/category/news"
            className="inline-flex items-center gap-1.5 mt-5 h-10 px-4 rounded-md bg-primary-700 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回影视资讯
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = newsList
    .filter((item) => item.id !== article.id)
    .sort((a, b) => (a.category === article.category ? -1 : 0))
    .slice(0, 4);

  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-5 md:pt-7">
        <Breadcrumbs
          items={[
            { label: "首页", to: "/" },
            { label: "影视资讯", to: "/category/news" },
            { label: article.category },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
          <article className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-7">
            <div className="flex items-center flex-wrap gap-2 text-xs font-label">
              <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-800">{article.category}</span>
              <span className="text-foreground-400">{article.date}</span>
              <span className="text-foreground-300">·</span>
              <span className="text-foreground-400">奇优影院编辑部</span>
            </div>

            <h1 className="text-xl md:text-3xl font-extrabold text-foreground-950 font-heading mt-3 leading-snug text-balance">
              {article.title}
            </h1>
            <p className="text-sm md:text-[15px] text-foreground-600 mt-3 leading-relaxed border-l-2 border-accent-400 pl-3">
              {article.summary}
            </p>

            <div className="w-full h-52 md:h-[340px] rounded-lg overflow-hidden bg-primary-900 mt-5">
              <img
                src={article.image}
                alt={`${article.title}配图`}
                title={`${article.title} 影视资讯配图`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-5 space-y-4 text-sm md:text-[15px] text-foreground-800 leading-[1.9]">
              {article.content.split("\n\n").map((paragraph, index) => (
                <p key={`${article.id}-p-${index}`}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-background-100 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-foreground-400">本文为虚构示例内容，仅用于站点展示。</p>
              <Link
                to="/category/news"
                className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
                返回资讯列表
              </Link>
            </div>
          </article>

          <aside className="space-y-4 lg:sticky lg:top-20">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-bold text-foreground-950 font-heading mb-3">相关文章推荐</h2>
              <ul className="space-y-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex gap-2.5 cursor-pointer">
                      <span className="w-10 h-10 rounded overflow-hidden shrink-0 bg-primary-900">
                        <img
                          src={item.image}
                          alt={`${item.title}配图`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-800 leading-snug group-hover:text-accent-700 transition-colors line-clamp-2">
                          {item.title}
                        </span>
                        <span className="block text-[11px] text-foreground-400 mt-0.5">{item.date}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-4">
              <p className="text-xs tracking-[0.14em] text-secondary-900 font-label uppercase mb-1.5">继续浏览</p>
              <div className="flex flex-col gap-1.5">
                <Link
                  to="/category/movie"
                  className="flex items-center justify-between text-sm text-foreground-800 hover:text-accent-700 transition-colors cursor-pointer"
                >
                  电影分类
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-400" />
                </Link>
                <Link
                  to="/category/tv"
                  className="flex items-center justify-between text-sm text-foreground-800 hover:text-accent-700 transition-colors cursor-pointer"
                >
                  电视剧分类
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-400" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}